/**
 * Lift Safety Validator (Client-Side Mirror)
 * 
 * Same pure validation logic as server-side for instant UI feedback.
 * Does NOT generate compatible lift suggestions — that's server-only
 * to avoid stale client state.
 * 
 * Rules:
 * - Hard block when gvwr_lbs > lift_capacity_lbs (both values present)
 * - No gating if either value is missing
 * - This is a safety system, NOT a lift-preference engine
 * - Does NOT influence technician selection (proficiency is king)
 */

export interface LiftSafetyResult {
  safe: boolean;
  gvwr?: number;
  capacity?: number;
  reason?: string;
}

/**
 * Validate whether a vehicle can safely be placed on a lift.
 * 
 * Returns { safe: true } if:
 *   - Either gvwrLbs or liftCapacityLbs is null/undefined (no gating when data missing)
 *   - gvwrLbs <= liftCapacityLbs
 * 
 * Returns { safe: false, gvwr, capacity, reason } when gvwrLbs > liftCapacityLbs
 */
export function validateLiftSafety(
  gvwrLbs: number | null | undefined,
  liftCapacityLbs: number | null | undefined
): LiftSafetyResult {
  // No gating if either value is missing
  if (gvwrLbs == null || liftCapacityLbs == null) {
    return { safe: true };
  }

  // No gating if either value is zero or negative (invalid data)
  if (gvwrLbs <= 0 || liftCapacityLbs <= 0) {
    return { safe: true };
  }

  if (gvwrLbs > liftCapacityLbs) {
    return {
      safe: false,
      gvwr: gvwrLbs,
      capacity: liftCapacityLbs,
      reason: `Vehicle GVWR (${gvwrLbs.toLocaleString()} lbs) exceeds lift capacity (${liftCapacityLbs.toLocaleString()} lbs)`,
    };
  }

  return { safe: true };
}

// ─── Core-100 R4 (CORE-AU, OD-22): planning never presents an unverified lift pairing as plainly compatible ───
// Constitution CORE-AU: "Where the vehicle's specification OR the equipment's rating is unknown, the system MUST NOT
// infer that the pairing is safe." `validateLiftSafety` above keeps its narrow meaning (a KNOWN overweight block);
// planners classify the pairing with `classifyLiftPairing` and must label anything but 'safe' / 'not_a_lift'. The
// physical gate (apply_vehicle_location_locked) refuses the same cases.
export const LIFT_BAY_TYPES: readonly string[] = Object.freeze(['2-post', '4-post', 'scissor', 'single-post']);

export interface LiftBayFacts {
  bay_type?: string | null;
  lift_capacity_lbs?: number | null;
  lift_brand?: string | null;
  lift_model_number?: string | null;
  lift_serial_number?: string | null;
  lift_capacity_source?: string | null;
  lift_tag_photo_url?: string | null;
}

/** Mirrors public.shop_bay_is_lift: a lift by type, or any bay that carries a lift record. */
export function isLiftBay(bay: LiftBayFacts): boolean {
  return LIFT_BAY_TYPES.includes(String(bay.bay_type ?? ''))
    || bay.lift_capacity_lbs != null || bay.lift_brand != null || bay.lift_model_number != null
    || bay.lift_serial_number != null || bay.lift_capacity_source != null || bay.lift_tag_photo_url != null;
}

export type LiftPairing =
  | { kind: 'not_a_lift' }
  | { kind: 'safe'; gvwr: number; capacity: number }
  | { kind: 'unsafe'; gvwr: number; capacity: number; reason: string }
  | { kind: 'verification_required'; basis: 'lift_rating_unknown' | 'vehicle_weight_unknown'; reason: string };

export function classifyLiftPairing(gvwrLbs: number | null | undefined, bay: LiftBayFacts): LiftPairing {
  if (!isLiftBay(bay)) return { kind: 'not_a_lift' };
  const capacity = bay.lift_capacity_lbs != null && bay.lift_capacity_lbs > 0 ? bay.lift_capacity_lbs : null;
  if (capacity == null) {
    return { kind: 'verification_required', basis: 'lift_rating_unknown',
      reason: 'This lift has no rated capacity on record; a manager must record it before a vehicle goes on it.' };
  }
  const gvwr = gvwrLbs != null && gvwrLbs > 0 ? gvwrLbs : null;
  if (gvwr == null) {
    return { kind: 'verification_required', basis: 'vehicle_weight_unknown',
      reason: 'The vehicle\'s weight is unknown; a manager must verify it against this lift\'s rating before it goes up.' };
  }
  if (gvwr > capacity) {
    return { kind: 'unsafe', gvwr, capacity,
      reason: `Vehicle GVWR (${gvwr.toLocaleString()} lbs) exceeds lift capacity (${capacity.toLocaleString()} lbs)` };
  }
  return { kind: 'safe', gvwr, capacity };
}
